import { Component, inject, signal, computed, ChangeDetectionStrategy, OnDestroy, AfterViewInit } from '@angular/core';
import { DragDropModule, CdkDragDrop, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { TranslateService } from '@ngx-translate/core';
import { BaseWidgetDirective } from '../widgets/widgetsbase.directive';
import { NotificationService } from '@shared/services/notification.service';
import { HOME_WIDGET_PANEL_MAX_WIDTH } from '@modules/core/models/app-state';
import { HomeWidgetVisibilityStore } from '@modules/core/store/home-widget-visibility.store';
import { EarlyDeparturesWidgetComponent, LateDeparturesWidgetComponent, PunctualityWidgetComponent, WarningsWidgetComponent, EvSocWidgetComponent } from '@components/widgets';

const STORAGE_KEY_COLUMNS = 'oms.widget.columns';
const STORAGE_KEY_COLUMN_WIDTHS = 'oms.widget.columnWidths';

@Component({
    selector: 'app-home-view',
    templateUrl: './home-view.component.html',
    styleUrls: ['./home-view.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        EarlyDeparturesWidgetComponent,
        LateDeparturesWidgetComponent,
        PunctualityWidgetComponent,
        WarningsWidgetComponent,
        EvSocWidgetComponent,
        DragDropModule
    ],
    host: {
        '(document:mousemove)': 'onWindowMouseMove($event)',
        '(document:mouseup)': 'onWindowMouseUp($event)'
    }
})
export class HomeViewComponent implements OnDestroy, AfterViewInit {
    private readonly _notification = inject(NotificationService);
    private readonly _ts = inject(TranslateService);
    private readonly _homeWidgetVisibility = inject(HomeWidgetVisibilityStore);
    private static readonly  DEFAULT_WIDGET_COLUMNS: readonly (readonly string[])[] = [
        ['widget-warnings', 'widget-punctuality', 'widget-late-departures', 'widget-early-departures'],
        ['widget-ev-soc'],
    ];

    public readonly columns = signal<string[][]>(
        this._normalizeColumns(this._loadPersistedOrder())
    );
    public readonly connectedLists = computed(() => this.columns().map((_, i) => `home-col-${i}`));
    public readonly colContentHeights = signal<number[]>([]);
    public readonly showWarningsWidget = computed(() => this._homeWidgetVisibility.isVisible('widget-warnings'));
    public readonly showPunctualityWidget = computed(() => this._homeWidgetVisibility.isVisible('widget-punctuality'));
    public readonly showLateDeparturesWidget = computed(() => this._homeWidgetVisibility.isVisible('widget-late-departures'));
    public readonly showEarlyDeparturesWidget = computed(() => this._homeWidgetVisibility.isVisible('widget-early-departures'));
    public readonly showEvSocWidget = computed(() => this._homeWidgetVisibility.isVisible('widget-ev-soc'));

    private _isResizing = false;
    private _resizeCurrentIndex = -1;
    private _resizeStartX = 0;
    private _leftStartWidth = 0;
    private readonly _minColWidth = 295; // px
    private readonly _maxTotalColWidth = HOME_WIDGET_PANEL_MAX_WIDTH;
    private _colObservers: ResizeObserver[] = [];
    private _colMutationObservers: MutationObserver[] = [];
    private _colRecalcFns: Array<() => void> = [];

    public ngAfterViewInit(): void {
        this._applyPersistedColumnWidths();
        this._observeColumnContentHeights();
        // Defer a second recalc pass to after the first browser paint so that
        // signal-driven class/style bindings (collapsed, fillRemaining, --widget-height)
        // are fully reflected in the DOM before we measure heights.
        requestAnimationFrame(() => this._colRecalcFns.forEach(fn => fn()));
    }
    
    public ngOnDestroy(): void {
        this._disconnectColObservers();
        try { document.documentElement.classList.remove('oms-resizing'); } catch {}
    }

    public drop(event: CdkDragDrop<string[]>, columnIndex: number): void {
        const movedId = this._resolveMovedWidgetId(event);
        if (!movedId) return;

        if (event.previousContainer === event.container && event.previousIndex === event.currentIndex) return;

        const cols = this.columns();
        const copy = cols.map(c => [...c]);

        if (event.previousContainer === event.container) {
            moveItemInArray(copy[columnIndex], event.previousIndex, event.currentIndex);
        } else {
            const prevIdx = cols.indexOf(event.previousContainer.data);
            if (prevIdx === -1) return;
            transferArrayItem(copy[prevIdx], copy[columnIndex], event.previousIndex, event.currentIndex);
        }

        this.columns.set(copy);
        this._updateFillRemainingAfterDrop(movedId, copy[columnIndex]);
        this._checkColumnFit(event, columnIndex, copy[columnIndex], movedId);
        this._persistOrder();
    }

    private _resolveMovedWidgetId(event: CdkDragDrop<string[]>): string | undefined {
        if (event.item?.data && typeof event.item.data === 'string') return event.item.data;
        if (event.previousContainer?.data && event.previousIndex != undefined) {
            const id = event.previousContainer.data[event.previousIndex];
            if (typeof id === 'string' && id.length) return id;
        }
        return undefined;
    }

    private _checkColumnFit(event: CdkDragDrop<string[]>, columnIndex: number, targetCol: string[], movedId: string): void {
        if (event.previousContainer === event.container) return; // same-column reorder: total height unchanged

        // Defer until after Angular re-renders so the widget is in its new DOM position
        // and _getColumnHeight() resolves against the correct column.
        requestAnimationFrame(() => {
            const colEl = document.getElementById(`home-col-${columnIndex}`);
            if (!colEl) return;
            const cs = getComputedStyle(colEl);
            const colHeight = Math.round(
                colEl.getBoundingClientRect().height
                - (Number.parseFloat(cs.paddingTop) || 0)
                - (Number.parseFloat(cs.paddingBottom) || 0)
            );
            if (!colHeight) return;

            const othersTotal = targetCol
                .filter(id => id !== movedId)
                .reduce((sum, id) => sum + BaseWidgetDirective.getEffectiveHeight(id), 0);

            const available = Math.max(0, colHeight - othersTotal);
            const movedHeight = BaseWidgetDirective.getEffectiveHeight(movedId);

            if (movedHeight <= available) return;

            BaseWidgetDirective.fitToColumn(movedId, available);

            const lastId = targetCol.at(-1);
            if (lastId) BaseWidgetDirective.setFillRemaining(lastId);
        });
    }

    private _updateFillRemainingAfterDrop(movedId: string, targetCol: string[]): void {
        try {
            if (targetCol.length > 1) {
                const movedPos = targetCol.indexOf(movedId);
                if (movedPos === targetCol.length - 1) {
                    const prevWidgetId = targetCol[targetCol.length - 2];
                    if (typeof prevWidgetId === 'string' && prevWidgetId.length) {
                        BaseWidgetDirective.clearFillRemaining(prevWidgetId);
                    }
                }
            }
        } catch {
            try { this._notification.openText(this._ts.instant('Unable to save widget layout preference'), 3000); } catch {}
        }
    }

    public onColumnResizeStart(event: Event, currentIndex: number): void {
        const mouseEvent = event as MouseEvent;
        // only left mouse
        if (mouseEvent.button !== 0) return;
        mouseEvent.preventDefault();

        const leftEl = document.getElementById(`home-col-${currentIndex}`);
        if (!leftEl) return;

        this._isResizing = true;
        this._resizeCurrentIndex = currentIndex;
        this._resizeStartX = mouseEvent.clientX;
        this._leftStartWidth = Math.round(leftEl.getBoundingClientRect().width);

        document.documentElement.classList.add('oms-resizing');
    }

    public onWindowMouseMove(e: MouseEvent): void {
        if (!this._isResizing) return;

        this._onColumnResizeMove(e);
    }

    public onWindowMouseUp(e: MouseEvent): void {
        if (!this._isResizing) return;

        this._onColumnResizeEnd(e);
    }

    private _persistOrder(): void {
        try {
            localStorage.setItem(STORAGE_KEY_COLUMNS, JSON.stringify(this.columns()));
        } catch (e: any) {
            try { this._notification.openText(this._ts.instant('Unable to save widget order'), 3000); } catch {}
        }
    }

    private _loadPersistedOrder(): string[][] | undefined {
        try {
            const raw = localStorage.getItem(STORAGE_KEY_COLUMNS);
            if (!raw) return undefined;
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed)) return parsed;
        } catch { /* ignore */ }
        return undefined;
    }

    private _onColumnResizeMove(e: MouseEvent): void {
        if (!this._isResizing) return;

        const delta = e.clientX - this._resizeStartX;
        const leftIdx = this._resizeCurrentIndex;
        const leftEl = document.getElementById(`home-col-${leftIdx}`);
        if (!leftEl) return;

        let newLeft = this._leftStartWidth + delta;

        // Sum widths of all other columns (excluding the current one) to
        // compute how large the left column may become without exceeding
        // the total width cap.
        let otherColsTotal = 0;
        try {
            const cols = this.columns();
            for (let i = 0; i < cols.length; i++) {
                if (i === leftIdx) continue;
                const el = document.getElementById(`home-col-${i}`);
                if (el) otherColsTotal += Math.round(el.getBoundingClientRect().width);
            }
        } catch {
            // ignore DOM read errors
        }

        // maximum left width allowed so total <= _maxTotalColWidth
        const maxLeft = Math.max(this._minColWidth, this._maxTotalColWidth - otherColsTotal);

        if (newLeft < this._minColWidth) newLeft = this._minColWidth;
        if (newLeft > maxLeft) newLeft = maxLeft;

        // lock left column width and ensure right column keeps its original width
        leftEl.style.flex = `0 0 ${Math.round(newLeft)}px`;
    }

    private _onColumnResizeEnd(_e: MouseEvent): void {
        if (!this._isResizing) return;

        this._isResizing = false;
        try { document.documentElement.classList.remove('oms-resizing'); } catch {}
        // Persist widths so they are restored on reload
        try { this._persistColumnWidths(); } catch {}
    }

    private _persistColumnWidths(): void {
        try {
            const cols = this.columns();
            const widths: number[] = [];
            for (let i = 0; i < cols.length; i++) {
                const el = document.getElementById(`home-col-${i}`);
                widths.push(el ? Math.round(el.getBoundingClientRect().width) : 0);
            }
            localStorage.setItem(STORAGE_KEY_COLUMN_WIDTHS, JSON.stringify(widths));
        } catch (e) {
            // ignore storage errors
        }
    }

    private _observeColumnContentHeights(): void {
        this._disconnectColObservers();
        this._colRecalcFns = [];
        const cols = this.columns();
        cols.forEach((_, i) => {
            const colEl = document.getElementById(`home-col-${i}`);
            if (!colEl) return;

            // Recalculate height by summing bounding rects of all direct children,
            // excluding CDK internal elements (placeholder, preview, animating clones).
            const recalc = () => {
                let h = 0;
                let visibleCount = 0;
                for (const child of Array.from(colEl.children)) {
                    const el = child as HTMLElement;
                    if (el.classList.contains('cdk-drag-placeholder')) continue;
                    // Component hosts use display:contents (height = 0); measure the inner .widget instead.
                    const measured = el.querySelector<HTMLElement>('[data-widget-id]') ?? el;
                    h += measured.getBoundingClientRect().height;
                    visibleCount++;
                }

                // Add the CSS gap between items so the handle height spans from the
                // top of the first widget to the bottom of the last widget.
                if (visibleCount > 1) {
                    const gap = parseFloat(getComputedStyle(colEl).rowGap) || 0;
                    h += gap * (visibleCount - 1);
                }
                this.colContentHeights.update(heights => {
                    const copy = [...heights];
                    copy[i] = h;
                    return copy;
                });
            };

            // One ResizeObserver that watches every child element.
            const ro = new ResizeObserver(recalc);
            const observeChildren = () => {
                ro.disconnect();
                for (const child of Array.from(colEl.children)) {
                    ro.observe(child);
                    // Also observe the inner .widget element so collapse/expand and
                    // fillRemaining height changes trigger recalc immediately, even
                    // when the host element's own size hasn't changed (e.g. flex-grow).
                    const innerWidget = (child as HTMLElement).querySelector<HTMLElement>(':scope > .widget');
                    if (innerWidget) ro.observe(innerWidget);
                }
                recalc();
            };
            observeChildren();
            this._colObservers.push(ro);
            this._colRecalcFns.push(recalc);

            // MutationObserver re-connects when CDK drag reorders or adds/removes widgets.
            const mo = new MutationObserver(observeChildren);
            mo.observe(colEl, { childList: true });
            this._colMutationObservers.push(mo);
        });
    }

    private _disconnectColObservers(): void {
        this._colObservers.forEach(o => o.disconnect());
        this._colObservers = [];
        this._colMutationObservers.forEach(o => o.disconnect());
        this._colMutationObservers = [];
        this._colRecalcFns = [];
    }

    private _applyPersistedColumnWidths(): void {
        try {
            const raw = localStorage.getItem(STORAGE_KEY_COLUMN_WIDTHS);
            if (!raw) return;

            const parsed = JSON.parse(raw) as any[];
            if (!Array.isArray(parsed)) return;
            
            const cols = this.columns();
            for (let i = 0; i < Math.min(cols.length, parsed.length); i++) {
                let w = Math.round(Number(parsed[i]));
                if (!Number.isFinite(w) || w <= 0) continue;
                w = Math.max(this._minColWidth, Math.min(w, this._maxTotalColWidth));
                const el = document.getElementById(`home-col-${i}`);
                if (el) el.style.flex = `0 0 ${w}px`;
            }
        } catch (e) {
            // ignore
        }
    }
    private _normalizeColumns(persistedColumns: string[][] | null | undefined): string[][] {
        const defaultColumns = HomeViewComponent.DEFAULT_WIDGET_COLUMNS;
        const validWidgetIds = new Set(defaultColumns.flat());

        if (!persistedColumns?.length) {
            return this._cloneColumns(defaultColumns);
        }

        const seenWidgetIds = new Set<string>();

        const normalizedColumns = persistedColumns.map(column =>
            column.filter(widgetId => {
                const isKnownWidget = validWidgetIds.has(widgetId);
                const isDuplicate = seenWidgetIds.has(widgetId);

                if (!isKnownWidget || isDuplicate) {
                    return false;
                }

                seenWidgetIds.add(widgetId);
                return true;
            })
        );

        defaultColumns.forEach((defaultColumn, columnIndex) => {
            normalizedColumns[columnIndex] ??= [];

            for (const widgetId of defaultColumn) {
                if (!seenWidgetIds.has(widgetId)) {
                    normalizedColumns[columnIndex].push(widgetId);
                    seenWidgetIds.add(widgetId);
                }
            }
        });

        return normalizedColumns;
    }

        private _cloneColumns(columns: readonly (readonly string[])[]): string[][] {
        return columns.map(column => [...column]);
    }
}



